import { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import { useToastDisappear } from '../../../../common/DataLoadingToast';
import { TOAST_TIMEOUT } from '@interstate/components/Toast/Toast/store';
import type { UpdatingDealTemplate } from '@makemydeal/dr-dash-types';

export const useRecentlyUpdatedTemplate = () => {
    const {
        id,
        isUpdating,
        isUpdated,
        isError: isUpdatingError,
        name: templateName,
        errorDetails: templateError,
        inventoryType
    } = useSelector(dealTemplateSelectors.getDealTemplateUpdatingStatus) as UpdatingDealTemplate;
    const { toastShown: successToastShown, triggerToast: showSuccessToast } = useToastDisappear(TOAST_TIMEOUT);
    const { toastShown: errorToastShown, triggerToast: showErrorToast } = useToastDisappear(TOAST_TIMEOUT);
    const wasUpdatingRef = useRef(false);
    useEffect(() => {
        if (isUpdating) {
            wasUpdatingRef.current = true;
        }
    }, [isUpdating]);

    const [hasTemplateConflictError, setHasTemplateConflictError] = useState(false);
    const [templatesWithConflict, setTemplatesWithConflict] = useState<string[]>([]);

    // Reset local conflict state when Redux state is cleared (when there's no error or updating status)
    useEffect(() => {
        if (!isUpdatingError && !isUpdating && !templateError) {
            setHasTemplateConflictError(false);
            setTemplatesWithConflict([]);
        }
    }, [isUpdatingError, isUpdating, templateError]);

    useEffect(() => {
        if (!wasUpdatingRef.current) return;
        if (isUpdated || isUpdatingError) {
            wasUpdatingRef.current = false;
        }
        if (isUpdatingError && templateError?.name === 'TemplateAutoApplyConflictException') {
            setHasTemplateConflictError(true);
            setTemplatesWithConflict(templateError.errors.map(({ name }) => name));
            return;
        }
        if (isUpdatingError) {
            showErrorToast();
        }

        if (isUpdated) {
            showSuccessToast();
        }
    }, [isUpdated, isUpdatingError]);

    return {
        id,
        shouldShowSuccessToast: Boolean(successToastShown && isUpdated && templateName),
        shouldShowErrorToast: Boolean(errorToastShown && templateName),
        hasTemplateConflictError,
        templatesWithConflict,
        setHasTemplateConflictError,
        templateName,
        inventoryType,
        isUpdating,
        isUpdatingError,
        errorDetails: templateError,
        isUpdated
    };
};
